/**
 * MetricCard Component
 * Displays a single financial metric with optional trend and details
 */

import React from 'react';
import './MetricCard.css';

export interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label: string;
    direction: 'up' | 'down' | 'neutral';
  };
  variant?: 'default' | 'success' | 'warning' | 'danger';
  loading?: boolean;
  onClick?: () => void;
  headerAction?: React.ReactNode;
  children?: React.ReactNode;
}

export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default',
  loading = false,
  onClick,
  headerAction,
  children,
}: MetricCardProps) {
  if (loading) {
    return (
      <article className="metric-card metric-card--loading" aria-busy="true">
        <div className="metric-card__header">
          <div className="skeleton skeleton--title"></div>
        </div>
        <div className="metric-card__body">
          <div className="skeleton skeleton--value"></div>
          <div className="skeleton skeleton--subtitle"></div>
        </div>
      </article>
    );
  }

  const isClickable = !!onClick;

  return (
    <article
      className={`metric-card metric-card--${variant}${isClickable ? ' metric-card--clickable' : ''}`}
      onClick={onClick}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `${title} - click to view details` : undefined}
    >
      <div className="metric-card__header">
        {icon && <div className="metric-card__icon">{icon}</div>}
        <h2 className="metric-card__title">{title}</h2>
        {headerAction && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <span
            className="metric-card__header-action"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {headerAction}
          </span>
        )}
        {isClickable && (
          <span className="metric-card__drill-hint" aria-hidden="true">&rsaquo;</span>
        )}
      </div>

      <div className="metric-card__body">
        <div className="metric-card__value">{value}</div>

        {subtitle && <p className="metric-card__subtitle">{subtitle}</p>}

        {trend && (
          <div
            className={`metric-card__trend metric-card__trend--${trend.direction}`}
            aria-label={`Trend: ${trend.label}`}
          >
            <span className="metric-card__trend-icon">
              {trend.direction === 'up' && '↑'}
              {trend.direction === 'down' && '↓'}
              {trend.direction === 'neutral' && '→'}
            </span>
            <span className="metric-card__trend-value">{trend.value}%</span>
            <span className="metric-card__trend-label">{trend.label}</span>
          </div>
        )}

        {children && <div className="metric-card__details">{children}</div>}
      </div>
    </article>
  );
}
